import React, { FC } from 'react';
import { Card, Typography } from 'antd';
import { ImageCenteredContent, ImageFit } from 'src/pages-all/details/tutor/components/commons';
import { getImageSrc } from 'logic/utils/image';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import { USER_PROFILE_DEFAULT_AVATAR_IMAGE } from 'assets/images/images';
import { getUserNameLabel } from 'logic/utils/user-label';
import { Stack } from 'components/stack/stack';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import { OfferResponse } from 'logic/api-models/api-models';
import { GetOfferCardTitle } from 'src/pages-all/search/components/common.logic';
import { useDispatch, useSelector } from 'react-redux';
import { getSubjectsByNumber } from 'logic/store/subject/subject.selectors';
import { GroupWrap } from 'components/group-wrap/group-wrap';
import { Badge } from 'components/badge/Badge';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { STAR, TROPHY } from 'assets/icons/icons';
import {
  BookALessonAndPriceBox,
  CardItemContainer,
  DescriptionHeadline,
  DescriptionHeadlineBox,
  DescriptionLevelSpan,
  DescriptionOwnSpecSpan,
  DescriptionRating,
  PriceSpan,
  StyledCard,
  StyledDescriptionCard,
  StyledDescriptionSubCard,
} from 'src/pages-all/search/components/elitetutor-offer/elitetutor-card.style';
import { MIN_TOTAL_RATINGS } from 'logic/configs/ratings';
import { Text as TextDefault } from 'components/text/Text';
import { roundToXDecimals } from 'logic/utils/number';
import { isTrialableOffer } from 'logic/helpers/tutorHelper';
import { parsePrice } from 'src/pages-all/user/user-profile/my-pricing/my-pricing.logic';
import { ActionButton } from 'src/pages-all/home/components/ActionButton';
import { navigationSlice } from 'logic/store/navigation/navigation.slice';
import { AppRoute } from 'app/app.route.const';
import { generatePath } from 'react-router-dom';

const { Meta } = Card;
const { Title, Paragraph } = Typography;

export const ElitetutorCard: FC<{
  item: OfferResponse | undefined;
  getOfferTitle: GetOfferCardTitle;
}> = ({ item, getOfferTitle }) => {
  const { minTablet } = useBreakpoints();

  return (
    <StyledCard
      mobile={!minTablet}
      bodyStyle={{ padding: '0', margin: '0' }}
      cover={
        !minTablet ? (
          <ImageCenteredContent
            width={'auto'}
            height={320}
            style={{
              maxHeight: '320px',
              maxWidth: '768px',
              overflow: 'hidden',
              display: 'block',
              objectFit: 'cover',
              borderRadius: '10px',
            }}
            src={getImageSrc(
              getIsTutoringSchool(item)
                ? item?.tutoringSchool?.images?.[0]?.url
                : item?.user?.imageUrl
            )}
            fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
            alt={getUserNameLabel(item?.user)}
            preview={false}
          />
        ) : undefined
      }
    >
      <Meta
        style={{ margin: '0', padding: '8px 8px 0 8px' }}
        avatar={
          minTablet ? (
            <Stack justifyContent="center" alignItems="center">
              <ImageFit
                width={300}
                height={240}
                style={{ maxHeight: '300px', overflow: 'hidden' }}
                src={getImageSrc(
                  getIsTutoringSchool(item)
                    ? item?.tutoringSchool?.images?.[0]?.url
                    : item?.user?.imageUrl
                )}
                fallback={USER_PROFILE_DEFAULT_AVATAR_IMAGE}
                alt={getUserNameLabel(item?.user)}
                preview={false}
              />
            </Stack>
          ) : undefined
        }
        description={<SearchItemCompRightSide item={item} getOfferTitle={getOfferTitle} />}
      />
    </StyledCard>
  );
};

const SearchItemCompRightSide: React.FC<{
  item: OfferResponse | undefined;
  getOfferTitle: GetOfferCardTitle;
}> = ({ item, getOfferTitle }) => {
  const dispatch = useDispatch();
  const subjectsByNumber = useSelector(getSubjectsByNumber) || {};
  const offerTitle = getOfferTitle(item, subjectsByNumber, true);
  const { maxMobileLarge, minTablet } = useBreakpoints();

  const rating = item?.user?.averageRating && roundToXDecimals(item.user.averageRating);
  const totalRatings = item?.user?.totalRatings;
  const shouldShowFullRating = Boolean(rating && (totalRatings || 0) >= MIN_TOTAL_RATINGS);
  const shouldShowStarOnlyRating = Boolean(
    rating && (totalRatings || 0) > 0 && (totalRatings || 0) < MIN_TOTAL_RATINGS
  );
  const _isTrialable = isTrialableOffer(item);

  const renderRatingIfCan = () =>
    shouldShowFullRating ? (
      <DescriptionRating>
        <CustomIcon src={STAR} alt="star" loading="lazy" height={18} /> {rating} ({totalRatings})
      </DescriptionRating>
    ) : shouldShowStarOnlyRating ? (
      <DescriptionRating>
        <TextDefault color={'white'} strong>
          {`${totalRatings} ${translate(
            translationKeys.searchResultsPage.common.itemList.ratings
          )}`}
        </TextDefault>
      </DescriptionRating>
    ) : (
      <div />
    );

  return (
    <StyledDescriptionCard mobile={maxMobileLarge}>
      <DescriptionHeadlineBox>
        <DescriptionHeadline>
          <CustomIcon
            src={TROPHY}
            alt="star"
            loading="lazy"
            height={24}
            style={{ marginRight: '4px' }}
          />
          {translate(translationKeys.searchResultsPage.common.itemList.verifiedTutor)}
        </DescriptionHeadline>
        {renderRatingIfCan()}
      </DescriptionHeadlineBox>
      <CardItemContainer className="noselect" mobile={maxMobileLarge}>
        <StyledDescriptionSubCard mobile={!minTablet}>
          <Stack flow="row" fullWidth>
            <Title level={2} style={{ fontSize: '18px', fontWeight: 400 }} ellipsis>
              {offerTitle}
            </Title>
            <DescriptionLevelSpan>
              {translate(translationKeys.searchResultsPage.common.itemList.eliteLevel)}
            </DescriptionLevelSpan>
            <DescriptionOwnSpecSpan>
              <Paragraph
                ellipsis={{
                  rows: 2,
                }}
              >
                {item?.offerDesc}
              </Paragraph>
            </DescriptionOwnSpecSpan>
            <Stack flow="row" gap={0.5}>
              <GroupWrap gap={0.5}>
                {item?.elitetutor?.approvedSubjects
                  ?.slice(0, maxMobileLarge ? 2 : 3)
                  .map((subjectNumber) => subjectsByNumber[subjectNumber as any])
                  .map((subject, index) => (
                    <Badge key={index}>{subject?.subjectName}</Badge>
                  ))}
                {(item?.elitetutor?.approvedSubjects?.length || 0) > (maxMobileLarge ? 2 : 3) && (
                  <Badge style={{ padding: '2px 4px' }}>...</Badge>
                )}
              </GroupWrap>
            </Stack>
            <BookALessonAndPriceBox>
              <ActionButton
                style={{ boxShadow: 'none' }}
                onClick={(e?: React.MouseEvent<HTMLElement>) => {
                  e?.preventDefault();
                  if (item?._id) {
                    dispatch(
                      navigationSlice.actions.navigateTo({
                        path: _isTrialable
                          ? AppRoute.External_TrialHour_Offer
                          : generatePath(AppRoute.BookEliteTutor, { offerID: item._id }),
                      })
                    );
                  }
                }}
              >
                {_isTrialable
                  ? translate(translationKeys.searchResultsPage.common.itemList.bookTrial)
                  : translate(translationKeys.searchResultsPage.common.itemList.book)}
              </ActionButton>
              <PriceSpan>
                {item?.elitetutor?.creditStartingPrice &&
                  (_isTrialable
                    ? translate(translationKeys.searchResultsPage.common.itemList.bookTrialInfo, {
                        price: parsePrice(item?.elitetutor?.creditStartingPrice),
                      })
                    : translate(translationKeys.searchResultsPage.common.itemList.bookInfo, {
                        price: parsePrice(item?.elitetutor?.creditStartingPrice),
                      }))}
              </PriceSpan>
            </BookALessonAndPriceBox>
          </Stack>
        </StyledDescriptionSubCard>
      </CardItemContainer>
    </StyledDescriptionCard>
  );
};
